import { CollabParticipant, CursorSession } from '@livekatsomo/models';
import { Transaction } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { CustomPluginState } from './types';
import { createTelepointers } from './utils';
import { findCursors } from './findCursors';
import { getValidPos } from './getValidPos';

/**
 * Updates the cursor decorations for a given participant based on their telepointer data.
 * If the version for cursor document is older than the current one, the cursor is not updated.
 * Instead, it waits for the new update to arrive.
 *
 * @param version - The version of the cursor document.
 * @param telepointerData - The telepointer data for the participant.
 * @param participants - A map of all participants in the collaboration session.
 * @param value - The current state of the plugin.
 * @param decorationSet - The current decoration set.
 * @param remove - An array of decorations to remove.
 * @param tr - The current transaction.
 * @param add - An array of decorations to add.
 */
export function updateCursors({
  version,
  telepointerData,
  participants,
  value,
  decorationSet,
  remove,
  tr,
  add,
}: {
  version: number;
  telepointerData: CursorSession;
  participants: Map<string, CollabParticipant>;
  value: CustomPluginState;
  decorationSet: DecorationSet;
  remove: Decoration[];
  tr: Transaction;
  add: Decoration[];
}): void {
  const { clientID: remoteClientID } = telepointerData;
  const participant = participants.get(remoteClientID);

  if (participant && remoteClientID !== value.clientID) {
    // Check if the version for cursor document is older than the current one
    if (version > telepointerData.version) {
      // If it is, don't update the cursor.
      // Instead, wait for the new update to arrive.
      return;
    }

    // Remove old cursors from decoration set
    const oldPointers = findCursors(remoteClientID, decorationSet);
    if (oldPointers.length) {
      remove.push(...oldPointers);
    }

    const { anchor, head } = telepointerData.selection;
    const rawFrom = anchor < head ? anchor : head;
    const rawTo = anchor >= head ? anchor : head;
    const isSelection = rawTo - rawFrom > 0;

    let from = 1;
    let to = 1;

    try {
      from = getValidPos(tr, isSelection ? Math.max(rawFrom - 1, 0) : rawFrom);
      to = isSelection ? getValidPos(tr, rawTo) : from;
    } catch (err) {
      console.warn('Invalid position', err);
    }

    add.push(
      ...createTelepointers(
        from,
        to,
        remoteClientID,
        isSelection,
        participant.name.toUpperCase(),
      ),
    );
  }
}
